import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, roleHome, startGoogleLogin } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Header";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "student" });
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const user = await register(form);
            toast.success("Welcome to TechBloom!");
            navigate(roleHome(user.role));
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid min-h-screen place-items-center bg-background px-6 py-12" data-testid="register-page">
            <div className="card-soft w-full max-w-md p-8">
                <Logo />
                <h1 className="mt-8 font-display text-2xl font-bold tracking-tight">Create your account</h1>
                <p className="mt-1 text-sm text-muted-foreground">Discover. Connect. Experience. Bloom.</p>
                <form onSubmit={submit} className="mt-8 space-y-5">
                    <div className="space-y-2">
                        <Label htmlFor="name">Full name</Label>
                        <Input id="name" data-testid="register-name-input" required value={form.name}
                            className="rounded-lg focus:ring-2 focus:ring-primary/50"
                            onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" data-testid="register-email-input" type="email" required value={form.email}
                            className="rounded-lg focus:ring-2 focus:ring-primary/50"
                            onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="password">Password</Label>
                        <Input id="password" data-testid="register-password-input" type="password" required minLength={6} value={form.password}
                            className="rounded-lg focus:ring-2 focus:ring-primary/50"
                            onChange={(e) => setForm({ ...form, password: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                        <Label>I am a</Label>
                        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                            <SelectTrigger data-testid="register-role-select" className="rounded-lg"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="student" data-testid="role-student">Student</SelectItem>
                                <SelectItem value="faculty" data-testid="role-faculty">Faculty / Researcher</SelectItem>
                                <SelectItem value="organization" data-testid="role-organization">Organization</SelectItem>
                            </SelectContent>
                        </Select>
                        {form.role !== "student" && (
                            <p className="text-xs text-muted-foreground">Faculty and organization accounts are verified by TechBloom before a badge is shown.</p>
                        )}
                    </div>
                    {error && <p data-testid="register-error" className="rounded-lg bg-accent/10 px-4 py-2.5 text-sm text-accent">{error}</p>}
                    <Button data-testid="register-submit-button" type="submit" disabled={busy} className="w-full rounded-full">
                        {busy ? "Creating account..." : "Create account"}
                    </Button>
                </form>
                <div className="my-6 flex items-center gap-4"><span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><span className="h-px flex-1 bg-border" /></div>
                <Button data-testid="register-google-button" variant="outline" className="w-full rounded-full" onClick={startGoogleLogin}>
                    Continue with Google
                </Button>
                <p className="mt-6 text-center text-sm text-muted-foreground">
                    Already have an account? <Link to="/login" data-testid="goto-login-link" className="font-semibold text-primary">Log in</Link>
                </p>
            </div>
        </div>
    );
}
